import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import type { WeeklyGoalProgress } from "@time-tracker/shared";

const MINS_IN_HOUR = 60;

/** Formats decimal hours as "2h" or "2h 30m". */
const formatHours = (hours: number): string => {
  const whole = Math.floor(hours);
  const mins = Math.round((hours - whole) * MINS_IN_HOUR);
  return mins > 0 ? `${whole}h ${mins}m` : `${whole}h`;
};

/**
 * Dashboard card for the device-local weekly goal. The math is `computeWeeklyGoalProgress`
 * in `@time-tracker/shared`; this component only renders its snapshot. The dashboard mounts
 * it only when a goal is set.
 */
export function WeeklyGoalCard(props: { progress: WeeklyGoalProgress }) {
  const { t } = useI18n();
  const weeklyGoal = () => props.progress;

  return (
    <Show when={weeklyGoal().goalHours > 0}>
      <div
        class="bg-[#121212] border border-white/10 rounded-[24px] p-6 shadow-none"
        data-testid="weekly-goal-card"
      >
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-white">{t(msg`Weekly Goal`)}</h3>
          <span
            class="font-mono text-[11px]"
            classList={{
              "text-emerald-400": weeklyGoal().isMet || weeklyGoal().onTrack,
              "text-amber-400": !weeklyGoal().isMet && !weeklyGoal().onTrack,
            }}
            data-testid="weekly-goal-status"
          >
            <Show
              when={weeklyGoal().isMet}
              fallback={weeklyGoal().onTrack ? t(msg`On track`) : t(msg`Behind`)}
            >
              {t(msg`Goal met`)}
            </Show>
          </span>
        </div>
        <div class="flex items-baseline gap-1.5 mb-3">
          <span
            class="font-mono text-2xl font-semibold text-white"
            data-testid="weekly-goal-tracked"
          >
            {formatHours(weeklyGoal().trackedHours)}
          </span>
          <span class="font-mono text-sm text-white/40">
            / {formatHours(weeklyGoal().goalHours)}
          </span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div
            class="h-full rounded-full bg-primary transition-[width] duration-500"
            style={{ width: `${weeklyGoal().progressPercent}%` }}
            data-testid="weekly-goal-bar"
          />
        </div>
        <p class="mt-3 font-mono text-[11px] text-white/50">
          <Show
            when={weeklyGoal().isMet}
            fallback={t(msg`{hours} to go · {percent}%`, {
              hours: formatHours(weeklyGoal().remainingHours),
              percent: weeklyGoal().progressPercent,
            })}
          >
            {t(msg`Nice work — goal reached for this week.`)}
          </Show>
        </p>
      </div>
    </Show>
  );
}
